import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import logger from '../../utils/logger';
import { logout } from '../../resources/api.resource';

interface LogoutButtonProps {
  variant?: 'text' | 'outlined' | 'contained';
  fullWidth?: boolean;
  showIcon?: boolean;
}

const LogoutButton: React.FC<LogoutButtonProps> = ({
  variant = 'text',
  fullWidth = false,
  showIcon = true
}) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const handleOpenDialog = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmLogout = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    logger.event('Logout called');
    handleCloseDialog();
    try {
      // Global logout: terminate the Entra SSO session too, so the next
      // sign-in actually re-prompts (local-only logout leaves the IdP session
      // alive and silently re-authenticates).
      const response = await logout(true);
      if (response?.logoutUrl) {
        window.location.href = response.logoutUrl;
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        onClick={handleOpenDialog}
        fullWidth={fullWidth}
        startIcon={showIcon ? <LogoutIcon /> : null}
        sx={{
          color: '#666',
          justifyContent: 'flex-start',
          textTransform: 'none',
          fontSize: '0.875rem',
          padding: '8px 16px',
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        Log out
      </Button>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            color="primary"
            variant="contained"
            autoFocus
          >
            Log out
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LogoutButton;
